import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearTipoSolicitud = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estado inicial del formulario añadiendo ciaaproautinsol (0 o 1)
  const [formData, setFormData] = useState({
    tsolcodigo: "",
    tsoldescripcion: "",
    tsolInEg: "",
    tsolstatus: "A",
    ciaaproautinsol: 0,
  })

  // ==========================================================================
  // CONSULTA DEL PARÁMETRO SGA DE LA COMPAÑÍA
  // ==========================================================================
  const { data: paramSGA, isLoading: loadSGA } = useQuery({
    queryKey: ["getParametroSGA_Crear"],
    queryFn: async () => {
      try {
        const response = await api.get("/TipoSolicitud/getParametroSGA")
        return response?.data?.data || { hasSGA: false }
      } catch (error) {
        console.error("Error al cargar parámetro SGA:", error)
        return { hasSGA: false }
      }
    },
  })

  const hasSGA = paramSGA?.hasSGA || false

  // Mutación para guardar la creación
  const {
    mutateAsync: SaveCreacionTipoSolicitud,
    isPending: isSavingCreacion,
    isError,
  } = useMutation({
    queryKey: ["isCreatingTipoSolicitud"],
    fn: async (data) => {
      const response = await api.post("/TipoSolicitud/createTipoSolicitud", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1), // Retorna a la grilla tras guardar
  })

  // Manejador de cambios para limpiar y limitar entradas
  const handleInputChange = (field, value) => {
    // Si el campo es el switch numérico, lo pasamos directamente
    if (field === "ciaaproautinsol") {
      setFormData((prev) => ({ ...prev, [field]: value }))
      return
    }

    let finalValue = value.toUpperCase()

    // Respetamos mayúsculas para código y descripción, pero la Naturaleza (tsolInEg) debe
    // conservar su valor original exacto ("solegrbod", "solingGSga", etc.)
    if (field === "tsolInEg" || field === "tsolstatus") {
      finalValue = value
    } else if (field === "tsolcodigo") {
      finalValue = finalValue.slice(0, 3)
    } else if (field === "tsoldescripcion") {
      finalValue = finalValue.slice(0, 60)
    }

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Ejecución y validación al intentar guardar
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones del frontend
    if (!formData.tsolcodigo.trim()) return showWarning("El Código del Tipo de Solicitud es obligatorio")
    if (!formData.tsoldescripcion.trim()) return showWarning("La Descripción es obligatoria")
    // if (!formData.tsolInEg.trim()) return showWarning("Debe seleccionar la Naturaleza de la solicitud")

    await SaveCreacionTipoSolicitud(formData)
  }

  // Configuración de la barra de acciones superior (Solo GRABAR)
  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TOP Bar - Botonera */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingCreacion || loadSGA}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nuevo Tipo de Solicitud</b>
        </div>

        <CustomBackdrop isLoading={isSavingCreacion || loadSGA} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Tipo de Solicitud
            </Typography>

            <Grid container spacing={3} alignItems="center">
              {/* CÓDIGO */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.tsolcodigo}
                  onChange={(e) => handleInputChange("tsolcodigo", e.target.value)}
                  error={isError && !formData.tsolcodigo}
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.tsoldescripcion}
                  onChange={(e) => handleInputChange("tsoldescripcion", e.target.value)}
                  error={isError && !formData.tsoldescripcion}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>

              {/* NATURALEZA (COMBO AMPLIADO) */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label="Naturaleza (Ingreso/Egreso) *"
                  value={formData.tsolInEg}
                  onChange={(e) => handleInputChange("tsolInEg", e.target.value)}
                  error={isError && !formData.tsolInEg}
                >
                  <MenuItem value="SOLEGRBOD">SOLICITUD DE TRANSFERENCIA ENTRE BODEGAS</MenuItem>
                  <MenuItem value="SOLINGBOD">SOLICITUD DE TRANSFERENCIA ENTRE BODEGAS ING</MenuItem>
                  <MenuItem value="SOLEGRLOC">SOLICITUD DE TRASNFERENCIA ENTRE LOCALIDAD</MenuItem>
                  <MenuItem value="SOLINGLOC">SOLICITUD DE TRASNFERENCIA ENTRE LOCALIDAD ING</MenuItem>
                  <MenuItem value="SOLEGRPRES">SOLICITUD DE PRESTAMOS A CLIENTES</MenuItem>
                  <MenuItem value="SOLINGDPRE">SOLICITUD DE DEVOLUCION DE PRESTAMOS A CLIENTES</MenuItem>
                  <MenuItem value="SOLINGAJU">SOLICITUD DE INGRESO POR AJUSTE</MenuItem>
                  <MenuItem value="SOLEGRAJU">SOLICITUD DE EGRESO POR AJUSTE</MenuItem>
                  {/* OPCIONES CONDICIONALES SGA */}
                  {hasSGA && <MenuItem value="SOLINGGSGA">SOLICITUD DE INGRESO GESTION SGA</MenuItem>}
                  {hasSGA && <MenuItem value="SOLEGRGSGA">SOLICITUD DE EGRESO GESTION SGA</MenuItem>}
                </TextField>
              </Grid>

              {/* NUEVO SWITCH: AUTO-APROBACIÓN */}
              <Grid item xs={12} sm={12}>
                <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f8f9fa", p: 1, borderRadius: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.ciaaproautinsol === 1}
                        onChange={(e) => handleInputChange("ciaaproautinsol", e.target.checked ? 1 : 0)}
                        color="secondary"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="bold">
                        Permitir Auto-Aprobación para este tipo de solicitud
                      </Typography>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearTipoSolicitud
